import { useEffect, useState, useRef } from 'react'
import WizrInput from '../WizrInput'
import WizrText from '../WizrText'
import WizrFlexSpacing from '../WizrFlexSpacing'
import WizrButton from '../WizrButton'
import WizrFlexLayout from '../WizrFlexLayout'
import { styled } from 'styled-components'
import { useContentLoadingSelector } from '../../redux/selectors/content'
import WizrLoader from '../WizrLoader'
import { dispatchCreateEvalDataset, dispatchGetEvalDatasets } from '../../redux/actions/content'

const StyledContainer = styled(WizrFlexLayout)`
  gap: 8px;
  width: 100%;
  position: relative;
`

const InputContainer = styled(WizrFlexLayout)`
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  background-color: #fff;
  width: 100%;
`

interface ICreateDataset {
  name?: string
  app_id?: string
  agent_id?: string
  onFormSubmission: (params: { [key: string]: any }) => void
  onCancel: () => void
}

const CreateDataset = ({
  name,
  app_id,
  agent_id,
  onFormSubmission,
  onCancel,
}: ICreateDataset): JSX.Element => {
  const loading = useContentLoadingSelector()
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Form state
  const [formState, setFormState] = useState({
    datasetname: name || ''
  })

  // Validation state
  const [showNameValidation, setShowNameValidation] = useState(false)

  // Refs for error scrolling
  const nameInputRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setFormState({
      datasetname: name || ''
    })
  }, [name])

  const doUpdateFields = (fieldName: string, fieldValue: any) => {
    setFormState(prev => ({
      ...prev,
      [fieldName]: fieldValue
    }))
  }

  const validateAllFields = () => {
    const isBlank = (text?: string) => !text || text.trim() === ''
    let isValid = true
    let firstErrorRef: React.RefObject<HTMLDivElement> | null = null

    // Reset validation states
    setShowNameValidation(false)

    // Validate name
    if (isBlank(formState.datasetname)) {
      setShowNameValidation(true)
      if (!firstErrorRef) firstErrorRef = nameInputRef
      isValid = false
    }

    // Scroll to first error
    if (!isValid && firstErrorRef?.current) {
      setTimeout(() => {
        firstErrorRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100)
    }

    return isValid
  }

  const handleSubmit = async () => {
    if (!validateAllFields()) {
      return
    }

    if (!app_id && !agent_id) {
      return
    }

    try {
      setIsSubmitting(true)
      // Create payload with agent_id or app_id based on what's provided
      const payload = agent_id 
        ? { name: formState.datasetname, agent_id: agent_id }
        : { name: formState.datasetname, app_id: app_id! }
      
      await dispatchCreateEvalDataset(payload)

      // Refresh the dataset list
      const id = agent_id || app_id
      const idType = agent_id ? 'agent_id' : 'app_id'
      if (id) {
        await dispatchGetEvalDatasets(idType, id)
      }
      onCancel()
    } catch (error) {
      // Error is already handled in the redux model
      console.error('Failed to create dataset:', error)
    } finally {
      setIsSubmitting(false)
    }
  }
  
  return (
    <StyledContainer background='#FFF' justifyContent='space-evenly'>
      <div style={{ width: '100%', position: 'relative' }}>
        {loading && (
          <WizrLoader
            loading={true}
            fullPage={false}
            overlay={true}
          />
        )}
        <WizrFlexLayout
          flexDirection='column'
          justifyContent='flex-start'
          background='#fff'
          alignItems='flex-start'
          style={{ width: '100%', gap: '16px' }}
        >
          <InputContainer ref={nameInputRef}>
            <WizrText type='body2'>Name</WizrText>
            <WizrInput
              style={{ width: '100%' }}
              border="1px solid #000"
              background='#F2F4F9'
              onChange={(value: any) => {
                doUpdateFields('datasetname', value)
                if (value && showNameValidation) {
                  setShowNameValidation(false)
                }
              }}
              value={formState.datasetname}
              placeholder="Enter Dataset Name"
              showError={showNameValidation}
              errorMessage={showNameValidation ? "Please enter a dataset name" : undefined}
              disabled={loading}
            />
          </InputContainer>

          <WizrFlexSpacing
            justifyContent='flex-end'
            flexDirection='row'
            size='large'
            background='#FFF'
          >
            <WizrButton 
              type='default' 
              text='Cancel' 
              onClick={onCancel} 
              style={{ borderRadius: '20px' }}
              disabled={loading}
            />
            <WizrButton 
              type='primary' 
              text='Save' 
              onClick={handleSubmit}
              disabled={loading || isSubmitting} 
              style={{ borderRadius: '20px' }}
            />
          </WizrFlexSpacing>
        </WizrFlexLayout>
      </div>
    </StyledContainer>
  )
}

export default CreateDataset

